import { api } from '@/services/api/AxiosBaseService'

/**
 * Cadastros do módulo de propostas. Tudo aqui é próprio do módulo — nenhum
 * destes registros vem de planos, parentescos ou serviços do resto do sistema.
 */

export interface ProposalBeneficiaryGroup {
  id: string
  name: string
  description: string | null
  defaultAgeLimit: number | null
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
}

export interface ProposalTier {
  id: string
  categoryId: string
  name: string
  discountCurveId: string | null
  discountCurveName?: string | null
  beneficiariesLabel: string | null
  /** Frase pronta: o rótulo livre ou os grupos com seus limites de idade. */
  beneficiariesText: string
  minAge: number | null
  maxAge: number | null
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
  groups: { id: string; name: string; ageLimit: number | null; minAge: number | null }[]
}

export interface ProposalCategory {
  id: string
  name: string
  slug: string
  description: string | null
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
  tiers: ProposalTier[]
}

export interface ProposalCoverageItem {
  id: string
  name: string
  description: string | null
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
  /** Nomes anteriores (renomear guarda o histórico). */
  nameHistory?: { name: string; until: string }[]
}

export type TextBlockKind = 'CONDICOES' | 'MODALIDADES' | 'COBERTURAS' | 'NR01' | 'OBSERVACOES' | 'OUTROS'

export interface ProposalTextBlock {
  id: string
  kind: TextBlockKind
  title: string | null
  content: string | null
  categoryId: string | null
  categoryName: string | null
  autoInclude: boolean
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
}

export type OptionKind =
  | 'PAYMENT_DAY' | 'FIRST_PAYMENT' | 'MIN_MONTHS' | 'PAYMENT_METHOD' | 'CONTRACT_TYPE'
  /** Cidade que abre a assinatura do PDF — não é a cidade do cliente. */
  | 'SIGNING_CITY'

export const OPTION_KIND_LABELS: Record<OptionKind, string> = {
  CONTRACT_TYPE: 'Modalidade de contratação',
  MIN_MONTHS: 'Permanência mínima',
  PAYMENT_DAY: 'Data do pagamento mensal',
  FIRST_PAYMENT: 'Primeiro pagamento',
  PAYMENT_METHOD: 'Forma de pagamento',
  SIGNING_CITY: 'Cidade da assinatura',
}

export interface ProposalOption {
  id: string
  kind: OptionKind
  label: string
  value: string | null
  description: string | null
  isDefault: boolean
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
}

export interface ProposalDiscountCurve {
  id: string
  name: string
  description: string | null
  sortOrder: number
  status: 'ATIVO' | 'INATIVO'
}

const BASE = '/settings/commercial'

async function get<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const r = await api.get<{ data: T }>(`${BASE}${path}`, { params })
  return r.data.data
}

export const ProposalSetupService = {
  // ---- Categorias e tipologias ----
  categories: () => get<ProposalCategory[]>('/proposal-categories'),
  saveCategory: (payload: { id?: string; name: string; slug?: string | null; description?: string | null; sortOrder?: number; status?: string }) =>
    payload.id
      ? api.put(`${BASE}/proposal-categories/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-categories`, payload),
  deleteCategory: (id: string) => api.delete(`${BASE}/proposal-categories/${id}`),

  saveTier: (payload: {
    id?: string
    proposalCategoryId: string
    name: string
    discountCurveId?: string | null
    beneficiariesLabel?: string | null
    minAge?: number | null
    maxAge?: number | null
    sortOrder?: number
    status?: string
    groups?: { id: string; age_limit?: number | null; min_age?: number | null }[]
  }) =>
    payload.id
      ? api.put(`${BASE}/proposal-tiers/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-tiers`, payload),
  deleteTier: (id: string) => api.delete(`${BASE}/proposal-tiers/${id}`),

  // ---- Grupos de beneficiários ----
  groups: () => get<ProposalBeneficiaryGroup[]>('/proposal-groups'),
  saveGroup: (payload: { id?: string; name: string; description?: string | null; defaultAgeLimit?: number | null; sortOrder?: number; status?: string }) =>
    payload.id
      ? api.put(`${BASE}/proposal-groups/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-groups`, payload),
  deleteGroup: (id: string) => api.delete(`${BASE}/proposal-groups/${id}`),

  // ---- Itens de cobertura ----
  items: () => get<ProposalCoverageItem[]>('/proposal-items'),
  saveItem: (payload: { id?: string; name: string; description?: string | null; sortOrder?: number; status?: string }) =>
    payload.id
      ? api.put(`${BASE}/proposal-items/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-items`, payload),
  deleteItem: (id: string) => api.delete(`${BASE}/proposal-items/${id}`),

  // ---- Blocos de texto ----
  textBlocks: (categoryId?: string) => get<ProposalTextBlock[]>('/proposal-text-blocks', { category_id: categoryId }),
  saveTextBlock: (payload: { id?: string; kind: TextBlockKind; title?: string | null; content?: string | null; proposalCategoryId?: string | null; autoInclude?: boolean; sortOrder?: number; status?: string }) =>
    payload.id
      ? api.put(`${BASE}/proposal-text-blocks/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-text-blocks`, payload),
  deleteTextBlock: (id: string) => api.delete(`${BASE}/proposal-text-blocks/${id}`),

  // ---- Opções selecionáveis ----
  options: (kind?: OptionKind) => get<ProposalOption[]>('/proposal-options', { kind }),
  saveOption: (payload: { id?: string; kind: OptionKind; label: string; value?: string | null; description?: string | null; isDefault?: boolean; sortOrder?: number; status?: string }) =>
    payload.id
      ? api.put(`${BASE}/proposal-options/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-options`, payload),
  deleteOption: (id: string) => api.delete(`${BASE}/proposal-options/${id}`),

  // ---- Curvas de desconto ----
  curves: () => get<ProposalDiscountCurve[]>('/proposal-curves'),
  saveCurve: (payload: { id?: string; name: string; description?: string | null; sortOrder?: number; status?: string }) =>
    payload.id
      ? api.put(`${BASE}/proposal-curves/${payload.id}`, payload)
      : api.post(`${BASE}/proposal-curves`, payload),
  deleteCurve: (id: string) => api.delete(`${BASE}/proposal-curves/${id}`),
}
